import React, { ReactNode } from 'react'

import { NavigationMenuProps } from '../NavigationMenu'

import { useLocation } from 'react-router-dom'

import Box from 'components_new/atoms/Box'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import Icon, { IconName } from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemButton from 'components_new/atoms/List/ListItemButton'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import ListItemSecondaryAction from 'components_new/atoms/List/ListItemSecondaryAction'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import { matches } from '../NavigationItem/utils'
import { NAVBAR_COLLAPSED_TOOLTIP_OFFSET } from 'components_new/organisms/Navbar/lib'

export interface NavigationTitleProps {
  actions?: ReactNode
  chip?: ChipProps
  collapsed: NavigationMenuProps['collapsed']
  expanded: boolean
  iconName?: IconName
  onAdd?: () => void
  onToggle: () => void
  title: string
}

const NavigationTitle = (props: NavigationTitleProps) => {
  const {
    actions,
    chip,
    collapsed,
    expanded,
    iconName,
    onAdd,
    onToggle,
    title
  } = props

  const color = iconName ? 'text.secondary' : 'text.disabled'

  const location = useLocation()
  const getActive = () => {
    if (!collapsed.value) {
      return false
    }

    if (collapsed.activePattern) {
      return matches(
        collapsed.activePattern,
        `${location.pathname}${location.search}`
      )
    }

    return false
  }
  const selected = getActive()

  return (
    <>
      <Tooltip
        slotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
        title={collapsed.value ? title : undefined}
        placement={'right'}
      >
        <ListItem disablePadding sx={{ height: 30, overflow: 'hidden' }}>
          <ListItemButton
            color="primary"
            disableRipple={true}
            href={collapsed.value ? collapsed.href : undefined}
            onClick={onToggle}
            selected={selected}
            sx={{
              borderRadius: 1,
              height: 30,
              px: iconName || (collapsed.value && collapsed.iconName) ? 1 : 0.5,
              py: 0.5,
              '&:hover': {
                bgcolor: !collapsed.value ? 'transparent' : undefined,
                '& .NavigationTitleIcon': {
                  color: 'text.primary'
                },
                '& .NavigationTitleText': {
                  color: 'text.primary'
                },
                '& .NavigationTitleAction': {
                  opacity: !collapsed.value ? 1 : 0,
                  transition: 'opacity 0.5s ease'
                },
                '& .NavigationTitleArrow': {
                  color: 'text.primary',
                  opacity: !collapsed.value ? 1 : 0,
                  transition: 'opacity 0.5s ease'
                }
              }
            }}
          >
            {iconName || (collapsed.value && collapsed.iconName) ? (
              <ListItemIcon sx={{ position: 'relative', minWidth: 28 }}>
                <Icon
                  className="NavigationTitleIcon"
                  fontSize="small"
                  name={iconName ?? collapsed.iconName}
                  sx={{
                    color: selected ? 'text.primary' : undefined,
                    cursor: !collapsed.value ? 'default' : undefined
                  }}
                />
              </ListItemIcon>
            ) : null}
            <ListItemText sx={{ opacity: collapsed.value ? 0 : 1 }}>
              <Box sx={{ alignContent: 'center', display: 'flex' }}>
                <Text
                  className="NavigationTitleText"
                  fontWeight="600"
                  sx={{ color: color, pt: '0.3em' }}
                  variant="navigation"
                >
                  {title}
                </Text>
                <Icon
                  className="NavigationTitleArrow"
                  fontSize="small"
                  name="ChevronRight"
                  sx={{
                    color: color,
                    ml: '0.2em',
                    mt: '0.05em',
                    transition: 'all 0.2s ease',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)'
                  }}
                />
              </Box>
            </ListItemText>

            {chip ? <Chip sx={{ ml: 1 }} size={'xsmall'} {...chip} /> : null}

            {onAdd || actions ? (
              <ListItemSecondaryAction
                className="NavigationTitleAction"
                sx={{
                  opacity: 0,
                  pointerEvents: collapsed.value ? 'none' : undefined,
                  right: 0,
                  transition: 'all 0.2s ease'
                }}
              >
                {onAdd ? (
                  <IconButton
                    edge="end"
                    onClick={(event) => {
                      event.stopPropagation()
                      onAdd()
                    }}
                    size="small"
                  >
                    <Icon fontSize="small" name="AddOutlined" />
                  </IconButton>
                ) : null}
                {actions}
              </ListItemSecondaryAction>
            ) : null}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    </>
  )
}

NavigationTitle.displayName = 'NavigationTitle'
export default NavigationTitle
